import { useEffect, useState } from 'react';

import { useAuthProcessor } from '@/app/hooks';
import { type Partner } from '@/configs/partner';

import { CheckStatus, EmailForm, OTPForm } from '../../ui';
import EmailStatus from './EmailStatus';

type ReadyState = 'waiting' | 'loading' | 'ready';

interface EmailContentProps {
  readonly partner: Partner;
}

const EmailContent = ({ partner }: EmailContentProps): JSX.Element => {
  const [checkStatus, setCheckStatus] = useState<CheckStatus | null>(null);

  const {
    isLoading: isAuthLoading,
    step,
    error: authError,
    submitForm,
    clickLink,
  } = useAuthProcessor(partner);

  // form is not ready until after loading auth
  const [readyState, setReadyState] = useState<ReadyState>('waiting');

  useEffect(() => {
    if (isAuthLoading) {
      setReadyState('loading');
    } else {
      if (readyState === 'loading') {
        setReadyState('ready');
      }
    }
  }, [isAuthLoading, readyState]);

  const shouldResetForm: boolean = [
    CheckStatus.ERROR_NOT_ELIGIBLE,
    CheckStatus.ERROR,
    CheckStatus.SUCCESS_EMAIL,
  ].includes(checkStatus!);

  return (
    <>
      {Boolean(checkStatus) && (
        <div className="overflow-hidden rounded-lg">
          <EmailStatus checkStatus={checkStatus!} />
        </div>
      )}

      {Boolean(checkStatus) && CheckStatus.SUCCESS_EMAIL ? (
        <OTPForm
          setCheckStatus={setCheckStatus}
          isLoading={isAuthLoading}
          step={step}
          submitForm={submitForm}
          clickLink={clickLink}
          error={authError}
          partner={partner}
        />
      ) : (
        <EmailForm
          setCheckStatus={setCheckStatus}
          step={step}
          submitForm={submitForm}
          shouldResetForm={shouldResetForm}
          error={authError}
          partner={partner}
          isReady={readyState === 'ready'}
        />
      )}
    </>
  );
};

export default EmailContent;
